import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type Props = {
  mode: "generate" | "regen";
  outputType: "Style" | "Pattern";
  variant?: "fullscreen" | "inline";
  compact?: boolean;
};

export const INLINE_BUSY_OVERLAY_MIN_HEIGHT_CLASS = "min-h-[32rem]";

const GENERATE_MESSAGES: Record<"Style" | "Pattern", string[]> = {
  Style: [
    "Sketching the silhouette…",
    "Draping the cloth on the form…",
    "Painting the palette onto the garment…",
    "Rendering front and back of the look…",
    "Almost there — stay on this screen.",
  ],
  Pattern: [
    "Reading the trend language…",
    "Drawing a new motif, not a copy of the tile…",
    "Recoloring the surface to your palette…",
    "Laying out a seamless repeat…",
    "Almost there — stay on this screen.",
  ],
};

const REGEN_MESSAGES: Record<"Style" | "Pattern", string[]> = {
  Style: [
    "Keeping the same person and cut…",
    "Redrawing a new surface on this look…",
    "Rendering a fresh front and back…",
    "Stay on this screen — regeneration is in progress.",
  ],
  Pattern: [
    "Keeping the same tile format…",
    "Drawing a new surface in the same story…",
    "Stay on this screen — regeneration is in progress.",
  ],
};

const DRESS_PATH =
  "M80 36 C72 38 66 46 64 54 L52 58 L48 74 L38 162 C38 172 56 180 80 180 C104 180 122 172 122 162 L112 74 L108 58 L96 54 C94 46 88 38 80 36 Z";
const NECK_PATH = "M68 54 C72 64 88 64 92 54";
const HANGER_PATH = "M80 14 L80 28 M46 32 Q80 18 114 32";

const MOTIF_PATH =
  "M80 42 C96 42 112 58 112 78 C112 102 96 118 80 138 C64 118 48 102 48 78 C48 58 64 42 80 42 Z";
const MOTIF_INNER =
  "M80 58 C90 58 98 68 98 80 C98 94 90 104 80 118 C70 104 62 94 62 80 C62 68 70 58 80 58 Z";

const FashionLookDoodle: React.FC<{ className?: string }> = ({
  className = "h-[168px] w-[134px]",
}) => (
  <svg viewBox="0 0 160 200" className={className} aria-hidden="true">
    <path
      d={HANGER_PATH}
      pathLength={1}
      className="image-generator-doodle-stroke image-generator-doodle-hanger"
    />
    <path
      d={DRESS_PATH}
      pathLength={1}
      className="image-generator-doodle-stroke image-generator-doodle-body"
    />
    <path
      d={NECK_PATH}
      pathLength={1}
      className="image-generator-doodle-stroke image-generator-doodle-neck"
    />
    <circle r="3.2" className="image-generator-doodle-nib">
      <animateMotion
        dur="3.6s"
        repeatCount="indefinite"
        rotate="auto"
        path={DRESS_PATH}
      />
    </circle>
  </svg>
);

const FashionPatternDoodle: React.FC<{ className?: string }> = ({
  className = "h-[168px] w-[134px]",
}) => (
  <svg viewBox="0 0 160 200" className={className} aria-hidden="true">
    <rect
      x="28"
      y="28"
      width="104"
      height="144"
      rx="6"
      pathLength={1}
      className="image-generator-doodle-stroke image-generator-doodle-hanger"
    />
    <path
      d={MOTIF_PATH}
      pathLength={1}
      className="image-generator-doodle-stroke image-generator-doodle-body"
    />
    <path
      d={MOTIF_INNER}
      pathLength={1}
      className="image-generator-doodle-stroke image-generator-doodle-neck"
    />
    <circle r="3.2" className="image-generator-doodle-nib">
      <animateMotion
        dur="3.6s"
        repeatCount="indefinite"
        rotate="auto"
        path={MOTIF_PATH}
      />
    </circle>
  </svg>
);

const DOODLE_STYLES = `
  .image-generator-doodle-stroke {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
  }
  .image-generator-doodle-hanger {
    stroke: #c4a484;
    stroke-width: 2.4;
    animation: image-generator-doodle-draw 3.6s ease-in-out infinite;
  }
  .image-generator-doodle-body {
    stroke: #1f1f1f;
    stroke-width: 2.6;
    animation: image-generator-doodle-draw 3.6s ease-in-out infinite;
    animation-delay: 0.12s;
  }
  .image-generator-doodle-neck {
    stroke: #7a5240;
    stroke-width: 2.2;
    animation: image-generator-doodle-draw 3.6s ease-in-out infinite;
    animation-delay: 0.55s;
  }
  .image-generator-doodle-nib {
    fill: #7a5240;
  }
  @keyframes image-generator-doodle-draw {
    0% { stroke-dashoffset: 1; opacity: 0.35; }
    8% { opacity: 1; }
    62% { stroke-dashoffset: 0; opacity: 1; }
    82% { stroke-dashoffset: 0; opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 0.28; }
  }
  @media (prefers-reduced-motion: reduce) {
    .image-generator-doodle-stroke {
      stroke-dashoffset: 0;
      animation: none;
      opacity: 1;
    }
    .image-generator-doodle-nib {
      display: none;
    }
  }
`;

const ImageGeneratorBusyOverlay: React.FC<Props> = ({
  mode,
  outputType,
  variant = "fullscreen",
  compact = false,
}) => {
  const messages =
    mode === "regen"
      ? REGEN_MESSAGES[outputType]
      : GENERATE_MESSAGES[outputType];
  const [messageIndex, setMessageIndex] = useState(0);
  const inline = variant === "inline";

  useEffect(() => {
    setMessageIndex(0);
    const timer = window.setInterval(() => {
      setMessageIndex(current => (current + 1) % messages.length);
    }, 4200);
    if (inline) {
      return () => window.clearInterval(timer);
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.clearInterval(timer);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [messages, inline]);

  const title =
    mode === "regen"
      ? outputType === "Style"
        ? "Refreshing this look"
        : "Refreshing this pattern"
      : outputType === "Style"
        ? "Creating your look"
        : "Creating your pattern";

  const doodle =
    outputType === "Pattern" ? (
      <FashionPatternDoodle
        className={compact ? "h-[72px] w-[58px]" : "h-[168px] w-[134px]"}
      />
    ) : (
      <FashionLookDoodle
        className={compact ? "h-[72px] w-[58px]" : "h-[168px] w-[134px]"}
      />
    );

  if (inline) {
    return (
      <div
        className={`z-10 flex flex-col items-center justify-center bg-[#F7F3EE] px-3 text-center ${
          compact
            ? "absolute inset-0"
            : `relative w-full ${INLINE_BUSY_OVERLAY_MIN_HEIGHT_CLASS}`
        }`}
        role="status"
        aria-busy="true"
        aria-label={title}
      >
        {doodle}
        {!compact && (
          <>
            <p className="mt-3 text-sm font-semibold text-gray-900">{title}</p>
            <p className="mt-1 text-xs text-gray-600 transition-opacity duration-300">
              {messages[messageIndex]}
            </p>
          </>
        )}
        <style>{DOODLE_STYLES}</style>
      </div>
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[#1F1F1F]/60 px-4 backdrop-blur-[3px]"
      role="dialog"
      aria-modal="true"
      aria-busy="true"
      aria-labelledby="image-generator-busy-title"
    >
      <div className="w-full max-w-md rounded-3xl bg-white px-8 py-10 text-center shadow-2xl font-avenir">
        <div className="mx-auto mb-5 flex h-[188px] w-[188px] items-center justify-center rounded-[28px] bg-[#F7F3EE]">
          {doodle}
        </div>
        <h2
          id="image-generator-busy-title"
          className="text-lg font-semibold text-gray-900"
        >
          {title}
        </h2>
        <p className="mt-2 min-h-[48px] text-sm text-gray-600 transition-opacity duration-300">
          {messages[messageIndex]}
        </p>
        <p className="mt-4 text-xs leading-5 text-gray-400">
          This can take a minute or two. Please wait here — leaving the page
          will interrupt the result.
        </p>
      </div>
      <style>{DOODLE_STYLES}</style>
    </div>,
    document.body
  );
};

export default ImageGeneratorBusyOverlay;
